import React, { useState, useEffect } from 'react';
import { fieldDomId, fieldProps } from './missingFields';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';
import SignatureUploadGuide from '../../../components/ui/SignatureUploadGuide';
import CountrySelect from './CountrySelect';
import LockedField from './LockedField';
import { useLocked } from '../hooks/useLocked';
import { isIndia } from '../../../utils/countries';
import { getPhoneCode, getPhonePlaceholder } from '../../../utils/phoneCodes';
import { capitalizeWords } from '../../../utils/formatters';
import Icon from '../../../components/AppIcon';
import { StepHeader, StepNote } from './StepHeader';
import DocumentReadPanel from './DocumentReadPanel';
import { distinctLegalName } from '../../../utils/vendorName';

/** A bordered, titled group so owner/director details read as distinct from the point of contact. */
const Section = ({ title, description, icon, children }) => (
  <section className="rounded-lg border border-border bg-card/40 p-4 sm:p-5">
    <div className="mb-4 flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon name={icon} size={16} />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {description && <p className="text-xs text-text-secondary">{description}</p>}
      </div>
    </div>
    {children}
  </section>
);

/**
 * Company information. In the registration flow the country and email are decided in the
 * Identity step; registry-verified values arrive locked (read-only). `showCountry` is only
 * true for the regularization form, which keeps its original layout.
 *
 * The step is split into three groups: the company/owner-director identity, the primary SPOC
 * (single point of contact), and a mandatory alternate SPOC to reach when the first is unavailable.
 */
const CompanyInformationStep = ({
  formData,
  updateFormData,
  errors,
  onPreviewDocument,
  keepExistingDocuments = {},
  setKeepExistingDocuments,
  uploadedFiles,
  showCountry = false,
  locked,
  lockSources,
  suggestedContacts = [],
  sessionId,
  kyc,
}) => {
  const lock = useLocked(locked, lockSources, 1);
  // The incorporation certificate is read on upload: it fills the CIN, the company name and the
  // year the company was formed, so an overseas vendor types three fewer things.
  const canRead = Boolean(sessionId) && kyc?.enabled !== false;
  const [incorporationFile, setIncorporationFile] = useState(null);
  const applyRead = (applied) => updateFormData(Object.fromEntries(applied.map((a) => [a.key, a.value])));

  // Digital signature is captured here in Step 2 so it is available for the Non-MSME declaration
  // (Step 4) and embedded into every agreement document (Step 6).
  const [signaturePreview, setSignaturePreview] = useState(null);
  useEffect(() => {
    if (formData.digitalSignature instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setSignaturePreview(reader.result);
      reader.readAsDataURL(formData.digitalSignature);
    } else if (!formData.digitalSignature) {
      setSignaturePreview(null);
    }
  }, [formData.digitalSignature]);

  const handleInputChange = (field, value) => {
    const fieldsToCapitalize = ['companyName', 'contactPersonName', 'designation', 'directorName', 'altSpocName', 'altSpocDesignation'];
    if (fieldsToCapitalize.includes(field)) {
      value = capitalizeWords(value);
    }
    updateFormData({ [field]: value });
  };

  const handleFileChange = (field, file) => updateFormData({ [field]: file });
  const handleRemoveFile = (field) => updateFormData({ [field]: null });

  const isIndiaSelected = isIndia(formData.countryOrigin);
  const prefilled = lock.isLocked('companyName');
  const suggestions = suggestedContacts.filter((name) => name && name !== formData.directorName).slice(0, 6);

  const phoneDescription = (value) =>
    formData.countryOrigin
      ? `Country code: ${getPhoneCode(formData.countryOrigin)}${value && value.startsWith(getPhoneCode(formData.countryOrigin)) ? ' ✓ Auto-formatted' : ''}`
      : 'Select a country to see the phone code';

  return (
    <div className="space-y-6">
      <StepHeader
        title="Company Information"
        description={
          prefilled
            ? 'Your registered details are pre-filled from government records. Confirm them and tell us who we should contact.'
            : "Your company's details, and the point(s) of contact we should reach."
        }
      >
        {prefilled ? (
          <StepNote tone="success" icon="ShieldCheck">
            Fields marked with a shield come from GST / MCA records and cannot be edited here. Everything else is yours to fill.
          </StepNote>
        ) : (
          <StepNote>Selecting a country formats the phone number with the right country code automatically.</StepNote>
        )}
      </StepHeader>

      {/* ---------- Company & Director ---------- */}
      <Section
        title="Company & Director Details"
        description="The registered business and the person who owns / heads it — not the point of contact."
        icon="Building2"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label="Business Vertical"
              type="text"
              value="Amber Enterprises India Limited"
              disabled={true}
              description="Fixed business vertical for all vendors"
              className="bg-muted/60"
            />
          </div>

          {lock.isLocked('companyName') ? (
            <LockedField
              label="Company Name"
              value={formData.companyName}
              source={lock.sourceOf('companyName')}
              required
              description={
                distinctLegalName(formData.companyName, formData.legalName)
                  ? `Trade name from GST records · legal name: ${distinctLegalName(formData.companyName, formData.legalName)}`
                  : undefined
              }
            />
          ) : (
            <Input
              label="Company Name"
              type="text"
              placeholder="Enter your company name"
              value={formData.companyName}
              {...fieldProps('companyName')}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              onBlur={() => handleInputChange('companyName', formData.companyName)}
              error={errors.companyName}
              required
            />
          )}

          {showCountry && (
            <CountrySelect
              value={formData.countryOrigin}
              triggerProps={fieldProps('countryOrigin')}
              onChange={(code) => handleInputChange('countryOrigin', code)}
              error={errors.countryOrigin}
            />
          )}

          <div className="space-y-2">
            <Input
              label="Name of the Director"
              type="text"
              placeholder="Enter the director's / owner's name"
              value={formData.directorName}
              {...fieldProps('directorName')}
              onChange={(e) => handleInputChange('directorName', e.target.value)}
              error={errors.directorName}
              required
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" aria-label="Suggested names from registry records">
                <span className="text-xs text-text-secondary">Suggested:</span>
                {suggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleInputChange('directorName', name)}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <Icon name="UserPlus" size={12} className="text-primary" />
                    {capitalizeWords(name.toLowerCase())}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Input
            label="Director Email"
            type="email"
            placeholder="Enter the director's email"
            value={formData.directorEmail}
            {...fieldProps('directorEmail')}
            onChange={(e) => handleInputChange('directorEmail', e.target.value)}
            onBlur={() => handleInputChange('directorEmail', formData.directorEmail)}
            error={errors.directorEmail}
            required
          />

          {lock.isLocked('yearEstablished') ? (
            <LockedField label="Year of Establishment" value={formData.yearEstablished} source={lock.sourceOf('yearEstablished')} />
          ) : (
            <Input
              label="Year of Establishment"
              type="number"
              placeholder="YYYY"
              min="1800"
              max={new Date().getFullYear()}
              value={formData.yearEstablished}
              {...fieldProps('yearEstablished')}
              onChange={(e) => handleInputChange('yearEstablished', e.target.value)}
              error={errors.yearEstablished}
              description={prefilled && formData.yearEstablished ? 'Suggested from your GST registration year — correct it if needed' : undefined}
            />
          )}

          <Input
            label="Website URL"
            type="url"
            placeholder="https://www.example.com"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            onBlur={() => handleInputChange('website', formData.website)}
            error={errors.website}
          />

          {!isIndiaSelected && (
            <div className="md:col-span-2">
              <FileUpload
                label="Company Incorporation Certificate"
                accept=".pdf"
                maxSize={4 * 1024 * 1024}
                {...fieldProps('incorporationCertificate')}
                value={formData.incorporationCertificate}
                onChange={(file) => { handleFileChange('incorporationCertificate', file); setIncorporationFile(file || null); }}
                onRemove={() => { handleRemoveFile('incorporationCertificate'); setIncorporationFile(null); }}
                onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'INCORPORATION_CERTIFICATE')}
                error={errors.incorporationCertificate}
                required
                description="Max 4MB - PDF format required"
              />
              {canRead && incorporationFile && (
                <DocumentReadPanel
                  sessionId={sessionId}
                  kind="INCORPORATION_CERTIFICATE"
                  file={incorporationFile}
                  onApplied={applyRead}
                  confirmedBy={formData.email || formData.directorEmail || ''}
                  onCleared={() => setIncorporationFile(null)}
                  className="mt-3"
                />
              )}
            </div>
          )}

          <div className="md:col-span-2">
            <Input
              label="Business Description"
              type="text"
              placeholder="Brief description of your business activities"
              value={formData.businessDescription}
              onChange={(e) => handleInputChange('businessDescription', e.target.value)}
              error={errors.businessDescription}
              description={prefilled && formData.businessDescription ? 'Pre-filled from your GST business activities — edit freely' : undefined}
            />
          </div>
        </div>
      </Section>

      {/* ---------- SPOC ---------- */}
      <Section
        title="SPOC Details"
        description="Your single point of contact for this registration and everything that follows."
        icon="UserRound"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="SPOC Name"
            type="text"
            placeholder="Enter the point of contact's name"
            value={formData.contactPersonName}
            {...fieldProps('contactPersonName')}
            onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
            error={errors.contactPersonName}
            required
          />

          {showCountry ? (
            <Input
              label="SPOC Email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              {...fieldProps('email')}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleInputChange('email', formData.email)}
              error={errors.email}
              required
            />
          ) : (
            <div className="space-y-2">
              <label htmlFor={fieldDomId('email')} className="text-sm font-medium leading-none text-foreground">
                SPOC Email <span className="text-destructive ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  {...fieldProps('email')}
                  type="email"
                  value={formData.email || ''}
                  readOnly
                  aria-readonly="true"
                  className="flex h-10 w-full cursor-default rounded-md border border-border bg-muted/60 pl-3 pr-10 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-success" aria-hidden="true">
                  <Icon name="ShieldCheck" size={16} />
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Verified in the Identity step.</p>
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
          )}

          <Input
            label="SPOC Designation"
            type="text"
            placeholder="Enter designation"
            value={formData.designation}
            {...fieldProps('designation')}
            onChange={(e) => handleInputChange('designation', e.target.value)}
            error={errors.designation}
            required
          />

          <Input
            label="SPOC Phone Number"
            type="tel"
            placeholder={getPhonePlaceholder(formData.countryOrigin)}
            value={formData.phoneNumber}
            {...fieldProps('phoneNumber')}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            onBlur={() => handleInputChange('phoneNumber', formData.phoneNumber)}
            error={errors.phoneNumber}
            required
            description={phoneDescription(formData.phoneNumber)}
          />
        </div>
      </Section>

      {/* ---------- Alternate SPOC ---------- */}
      <Section
        title="Alternate SPOC Details"
        description="A second person we can reach when the primary SPOC is unavailable. All four details are required."
        icon="Users"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Alternate SPOC Name"
            type="text"
            placeholder="Enter the backup contact's name"
            value={formData.altSpocName}
            {...fieldProps('altSpocName')}
            onChange={(e) => handleInputChange('altSpocName', e.target.value)}
            error={errors.altSpocName}
            required
          />

          <Input
            label="Alternate SPOC Email"
            type="email"
            placeholder="Enter email address"
            value={formData.altSpocEmail}
            {...fieldProps('altSpocEmail')}
            onChange={(e) => handleInputChange('altSpocEmail', e.target.value)}
            onBlur={() => handleInputChange('altSpocEmail', formData.altSpocEmail)}
            error={errors.altSpocEmail}
            required
          />

          <Input
            label="Alternate SPOC Designation"
            type="text"
            placeholder="Enter designation"
            value={formData.altSpocDesignation}
            {...fieldProps('altSpocDesignation')}
            onChange={(e) => handleInputChange('altSpocDesignation', e.target.value)}
            error={errors.altSpocDesignation}
            required
          />

          <Input
            label="Alternate SPOC Phone Number"
            type="tel"
            placeholder={getPhonePlaceholder(formData.countryOrigin)}
            value={formData.altSpocPhone}
            {...fieldProps('altSpocPhone')}
            onChange={(e) => handleInputChange('altSpocPhone', e.target.value)}
            onBlur={() => handleInputChange('altSpocPhone', formData.altSpocPhone)}
            error={errors.altSpocPhone}
            required
            description={phoneDescription(formData.altSpocPhone)}
          />
        </div>
      </Section>

      {/* ---------- Digital Signature (moved here from the Agreements step so it is available for the
               Non-MSME declaration in Step 4 and embedded into every agreement in Step 6) ---------- */}
      <Section
        title="Digital Signature"
        description="Uploaded once here — it is embedded into your Non-MSME declaration (if you generate one) and every agreement document."
        icon="FileSignature"
      >
        <div className="space-y-4">
          <SignatureUploadGuide />

          {uploadedFiles?.DIGITAL_SIGNATURE && !formData.digitalSignature?.name && (
            <div className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="keepDigitalSignature"
                    checked={keepExistingDocuments.digitalSignature || false}
                    onChange={(e) => setKeepExistingDocuments && setKeepExistingDocuments((prev) => ({ ...prev, digitalSignature: e.target.checked }))}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="keepDigitalSignature" className="cursor-pointer text-sm text-foreground">
                    Keep existing: <span className="font-medium">{uploadedFiles.DIGITAL_SIGNATURE.file_name}</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.DIGITAL_SIGNATURE, 'DIGITAL_SIGNATURE')}
                  className="flex items-center space-x-1 rounded-md px-3 py-1 text-sm text-primary transition-colors hover:bg-primary/5 hover:text-primary/80"
                >
                  <Icon name="eye" className="h-4 w-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>
          )}

          {(!keepExistingDocuments.digitalSignature || !uploadedFiles?.DIGITAL_SIGNATURE) && (
            <FileUpload
              label="Upload Digital Signature"
              accept=".jpg,.jpeg,.png"
              maxSize={10 * 1024}
              {...fieldProps('digitalSignature')}
              value={formData.digitalSignature}
              onChange={(file) => updateFormData({ digitalSignature: file })}
              onRemove={() => updateFormData({ digitalSignature: null, agreements: {}, agreementDownloads: {} })}
              onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'DIGITAL_SIGNATURE')}
              error={errors.digitalSignature}
              description="JPG or PNG format • Max 10KB • Recommended: 400×200px with transparent background"
              required
            />
          )}

          {signaturePreview && (
            <div className="rounded-lg border border-border bg-muted/30 p-6">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Signature Preview</h4>
                <span className="flex items-center text-xs text-success">
                  <Icon name="CheckCircle2" className="mr-1 h-4 w-4" /> Uploaded Successfully
                </span>
              </div>
              <div className="flex min-h-[120px] items-center justify-center rounded border border-border bg-white p-4">
                <img src={signaturePreview} alt="Digital Signature Preview" className="max-h-[80px] max-w-full object-contain" style={{ imageRendering: 'high-quality' }} />
              </div>
              <p className="mt-2 text-center text-xs text-text-secondary">This is how your signature will appear on your declaration and agreements</p>
            </div>
          )}

          <div className="rounded-lg border border-warning/30 bg-warning/10 p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />
              <div className="text-sm text-text-secondary">
                <p className="mb-1 font-semibold">Important:</p>
                <ul className="ml-4 list-disc space-y-1">
                  <li>The uploaded signature must be that of an authorized signatory</li>
                  <li>This signature will be legally binding on the declaration and all agreements</li>
                  <li>Ensure the signature is clear, professional, and properly formatted</li>
                  <li>Once submitted, you cannot change the signature without admin approval</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CompanyInformationStep;
